import { catchError, pipe } from 'overmind';

import type {
  Tenants as TenantsState,
  Tenant
} from '@/common/overmind/namespaces/tenants';
import type { Context } from '@/overmind';
import type { Organization, Tenants } from '@/overmind/effects/api/identity';
import type { GUID } from '@/overmind/effects/auth';
import { SelectedTenant } from '@/overmind/effects/selectedTenant';
import { AbortError, UnexpectedStateError } from '@/overmind/errors';
import { isUserSignedInToVolueIdentity } from '@/overmind/machines/auth';

export const selectTenantById = ({ state }: Context, tenantId: GUID) => {
  if (state.tenants.availableTenants.type === 'AVAILABLE') {
    const tenants = state.tenants.availableTenants.context.tenants;
    const currentTenant = tenants.find(tenant => tenant.id === tenantId);
    if (!currentTenant) {
      throw Error(
        `Cannot select tenant which is not available for the user. Provided tenant: ${tenantId}`
      );
    }

    if (tenantIsAlreadySelected(state.tenants, currentTenant)) {
      // TODO: This is a workaround to prevent unnecessary state changes. Investigate why tenant is selected so many times (4?);
      return;
    }

    state.tenants.currentTenant = {
      type: 'SELECTED_TENANT',
      context: {
        tenantId: currentTenant.id,
        name: currentTenant.name,
        organizationName: currentTenant.organizationName
      }
    };
    state.tenants.hasAccessToApplication = { type: 'NOT_CHECKED' };
  }
};

export const checkApplicationAccess = async (
  context: Context,
  tenantId: GUID
) => {
  const hasAccess = await context.effects.applicationAccess.hasAccess(tenantId);
  if (hasAccess) {
    context.state.tenants.hasAccessToApplication = {
      type: 'HAS_ACCESS'
    };
  } else {
    context.state.tenants.hasAccessToApplication = {
      type: 'NO_ACCESS'
    };
  }
};

export const changeTenantTo = async (context: Context, tenantId: GUID) => {
  if (
    context.state.tenants.currentTenant.type === 'SELECTED_TENANT' &&
    context.state.tenants.currentTenant.context.tenantId === tenantId
  ) {
    return;
  }
  context.state.tenants.currentTenant = { type: 'NO_TENANT_SELECTED' };
  context.effects.log.info(`Changing tenant to ${tenantId} started`);
  await context.actions.tenants.clearCurrentTenantRelatedState();
  context.effects.log.info(`Current tenant's related state cleared`);
  context.actions.tenants.selectTenantById(tenantId);
  context.effects.log.info(`Tenant ${tenantId} selected`);
  context.actions.showHome({ tenantId });
  context.effects.log.info(`Changing tenant to ${tenantId} completed`);

  context.actions.tenants.trySavingTenant({ tenantId });
};

export const trySavingTenant = async (
  context: Context,
  payload: { tenantId: GUID }
) => {
  if (
    context.state.tenants.currentTenant.type === 'SELECTED_TENANT' &&
    context.state.config.type === 'AVAILABLE'
    // && isUserSignedInToVolueIdentity(context.state.auth) // We don't need to be authenticated to save the tenant, leaving it for later inspection that we checked authentication
  ) {
    await context.effects.selectedTenant.save({
      tenantId: payload.tenantId,
      applicationId: context.state.config.config.auth.applicationId
    });
  }
};

export const clearCurrentTenantRelatedState = async (context: Context) => {
  await context.actions.arcgis.resetArcGisState();
  context.actions.selectedFeature.unselectFeature();
  context.actions.calculation.unloadCalculation();
  context.actions.map.resetMapCenter();
};

export const setAvailableTenants = (
  context: Context,
  availableTenants: Tenants
) => {
  context.state.tenants.availableTenants = {
    type: 'AVAILABLE',
    context: { tenants: availableTenants }
  };
};

export const loadAvailableTenants = pipe(
  async (context: Context, requestController?: AbortController) => {
    if (context.state.tenants.userOrganizations.current === 'LOADING') {
      requestController?.abort();
      throw new AbortError();
    }
    const userId = await context.effects.auth.getUserId();
    const applicationId = getApplicationId(context.state);
    if (!userId || !applicationId) {
      throw new UnexpectedStateError();
    }

    context.state.tenants.userOrganizations.send('TRIGGER_LOADING');
    return context.effects.api.identity.getAvailableOrganizations(
      userId,
      applicationId,
      requestController
    );
  },
  async (context: Context, result) => {
    if (result.type === 'SUCCESS' && result.context.organizations) {
      context.state.tenants.userOrganizations.send(
        'FETCH_SUCCESS',
        result.context.organizations
      );
      const availableTenants = toAvailableTenants(result.context.organizations);
      context.actions.tenants.setAvailableTenants(availableTenants);
      await context.actions.tenants.tryPreselectingTenant(availableTenants);
    } else {
      if (result.type === 'ERROR') {
        context.state.tenants.userOrganizations.send(
          'FETCH_ERROR',
          result.context.error.message
        );
      }
      throw new UnexpectedStateError();
    }
  },
  catchError(({ state }: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      state.tenants.availableTenants = { type: 'NOT_AVAILABLE' };
    }
  })
);

function tenantIsAlreadySelected(tenants: TenantsState, currentTenant: Tenant) {
  return (
    tenants.currentTenant.type === 'SELECTED_TENANT' &&
    currentTenant.id === tenants.currentTenant.context.tenantId
  );
}

export async function tryPreselectingTenant(
  context: Context,
  availableTenants: Tenants
) {
  if (
    context.state.calculation.externallyTriggeredCalculationSetExecution
      .current !== 'IDLE'
  ) {
    context.actions.tenants.selectTenantById(
      context.state.calculation.externallyTriggeredCalculationSetExecution
        .calculationContext.tenantId
    );
    return;
  }

  if (context.state.tenants.tenantFromRoute.type === 'ROUTED') {
    context.actions.tenants.selectTenantFromRoute({
      tenantId: context.state.tenants.tenantFromRoute.data.tenantId,
      availableTenants
    });
  } else if (
    isUserSignedInToVolueIdentity(context.state.auth) &&
    context.state.config.type === 'AVAILABLE'
  ) {
    await context.actions.tenants.tryUsingPreviouslySelectedTenant({
      applicationId: context.state.config.config.auth.applicationId,
      userId: context.state.auth.user.preferredUsername,
      availableTenants
    });
  } else {
    context.actions.tenants.selectFirstAvailableTenant(availableTenants);
  }
}

export function selectTenantFromRoute(
  context: Context,
  { availableTenants, tenantId }: { availableTenants: Tenants; tenantId: GUID }
) {
  const selectedTenant = new SelectedTenant(tenantId);
  if (selectedTenant.availableIn(availableTenants)) {
    context.actions.tenants.selectTenantById(selectedTenant.toGuid());
  } else {
    context.actions.tenants.selectFirstAvailableTenant(availableTenants);
  }
  context.state.tenants.tenantFromRoute = { type: 'NONE' };
}

export async function tryUsingPreviouslySelectedTenant(
  context: Context,
  {
    availableTenants,
    applicationId
  }: { userId: string; applicationId: string; availableTenants: Tenants }
) {
  const previouslySelectedTenant =
    await context.effects.selectedTenant.readPreviouslySelected({
      applicationId
    });
  if (previouslySelectedTenant.availableIn(availableTenants)) {
    context.actions.tenants.selectTenantById(previouslySelectedTenant.toGuid());
  } else {
    context.actions.tenants.selectFirstAvailableTenant(availableTenants);
  }
}

export function selectFirstAvailableTenant(
  context: Context,
  availableTenants: Tenants
) {
  const currentTenant = availableTenants[0];
  context.actions.tenants.selectTenantById(currentTenant.id as GUID);
}

export function getApplicationId(state: Context['state']) {
  return state.config.type === 'AVAILABLE'
    ? state.config.config.auth.applicationId
    : undefined;
}

export async function getTenantForUserSession(
  context: Context,
  payload: { applicationId: GUID }
): Promise<GUID> {
  const tenantIdFromUrl = context.effects.router.tenantId;
  let tenant = tenantIdFromUrl;
  if (!tenant) {
    const tenantId =
      await context.effects.selectedTenant.readPreviouslySelected({
        applicationId: payload.applicationId
      });

    tenant = tenantId.toGuid();
  }

  return tenant;
}

function toAvailableTenants(organizations: Organization[]): Tenants {
  return organizations.flatMap(org => {
    const tenants = org.tenants.map(tenant => ({
      id: tenant.id as GUID,
      name: tenant.name,
      organizationName: org.name
    }));
    return tenants;
  });
}
